/**
 * Shared source + methodology note rendered beneath the main data tables
 * on Rankings, Detail, Compare, and Providers per Georgetown's 2026-07-02
 * feedback pass. The Georgetown team supplied one canonical source
 * paragraph and one canonical methodology note; three of the four pages
 * (Rankings / Detail / Compare) share the same wording verbatim, and
 * Providers uses a shorter IPEDS-only variant. Centralizing here so a
 * copy tweak lands in one place across the tool.
 *
 * The `variant` prop picks which pair to render:
 *   - `metros` — Rankings / Detail / Compare (default). Cites OEWS,
 *                ACS, Employment Projections, and IPEDS; notes the
 *                credential-shortage rounding caveat.
 *   - `providers` — Providers page. Cites IPEDS only; notes the
 *                   round-to-nearest-5 program rule and the "zero =
 *                   <3 credentials" convention.
 */
interface TableSourceNoteProps {
  readonly variant?: 'metros' | 'providers';
  /** Optional Tailwind class overrides for the wrapping <aside>. */
  readonly className?: string;
}

const METROS_SOURCE =
  'Source: Georgetown University Center on Education and the Workforce analysis of data from the US Bureau of Labor Statistics, Occupational Employment and Wage Statistics (OEWS), 2012–24; the US Census Bureau, American Community Survey (ACS), 2012–19 and 2021–23; the US Bureau of Labor Statistics, Employment Projections, 2025; and the US Department of Education, Integrated Postsecondary Education Data System (IPEDS), 2021–23.';

const METROS_NOTE =
  'Note: Annual credentials awarded represent the average number of credentials produced annually in programs that align with the springboard occupations identified in that metro area. Annual job openings represent average annual projected job openings that align with those programs from 2024–34. The annual credential shortage may not equal the difference between the number of job openings and the number of credentials due to rounding. The credentials-to-jobs ratio is calculated using unrounded numbers.';

const PROVIDERS_SOURCE =
  'Source: Georgetown University Center on Education and the Workforce analysis of data from the US Department of Education, Integrated Postsecondary Education Data System (IPEDS), 2021–23.';

const PROVIDERS_NOTE =
  'Note: Annual credentials awarded represent the number of credentials produced annually in programs that align with the springboard occupations identified in that metro area. Program-level credentials awarded may not sum to totals due to rounding. Values have been rounded to the nearest 5, while programs with a value of zero indicate programs with fewer than 3 credentials awarded.';

export function TableSourceNote({
  variant = 'metros',
  className = '',
}: TableSourceNoteProps) {
  const source = variant === 'providers' ? PROVIDERS_SOURCE : METROS_SOURCE;
  const note = variant === 'providers' ? PROVIDERS_NOTE : METROS_NOTE;
  return (
    <aside
      aria-label="Data source and methodology"
      className={`mt-4 flex flex-col gap-2 rounded-md border border-border-light bg-ice/40 px-4 py-3 text-[12px] leading-[1.55] text-text-3 ${className}`}
    >
      <p>{source}</p>
      <p>{note}</p>
    </aside>
  );
}
